import React, { useEffect } from 'react';
import { FORM, LAYOUT_ITEM } from 'constants/FormConstants';
import { TextFieldLayoutItem, RadioButtonLayoutItem } from 'models/InputLayoutItem';
import { RutValidator } from 'utils/RutValidator';
import * as yup from 'yup';
import { CreateFormLayout } from 'components/create-form/CreateFormLayout';
import { MultiFormCardHeader } from 'components/MultiFormCardHeader';
import { DateStringValidator } from 'utils/DateValidator';
import { DATA_LAYERS } from 'constants/DataLayers';
import TagManager from 'react-gtm-module';
import { DataLayerMarkupProps } from 'models/props/DataLayerMarkupProps';

export const LoanPersonalInfoForm = ({ dataLayerKey }: DataLayerMarkupProps) => {
	const fields: any[] = [
		{
			label: 'Nombre',
			defaultValue: '',
			name: FORM.FIRST_NAME,
			type: LAYOUT_ITEM.TYPE.INPUT,
			placeholder: 'Ej: Juan Manuel',
			validation: yup
				.string()
				.required('Campo Requerido')
				.matches(
					/^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u,
					'Ingrese solo letras'
				),
		},
		{
			label: 'Apellidos',
			defaultValue: '',
			name: FORM.LAST_NAME,
			type: LAYOUT_ITEM.TYPE.INPUT,
			placeholder: 'Ej: Soto Marín',
			validation: yup
				.string()
				.required('Campo Requerido')
				.matches(
					/^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u,
					'Ingrese solo letras'
				),
		},
		{
			label: 'Rut',
			defaultValue: '',
			name: FORM.NATIONAL_ID,
			type: LAYOUT_ITEM.TYPE.INPUT,
			placeholder: 'Ej: 12345678-9',
			mask: 'rut',
			validation: yup
				.string()
				.required('Campo Requerido')
				.min(8, 'ingrese un rut válido')
				.test('rut-valid', 'ingrese un rut válido', (value) => {
					return value !== undefined ? RutValidator(value) : false;
				}),
		} as TextFieldLayoutItem,
		{
			label: 'Sueldo líquido',
			defaultValue: '',
			name: FORM.SALARY,
			type: LAYOUT_ITEM.TYPE.INPUT,
			prefix: '$',
			mask: 'money',
			tooltip: 'Suma de dinero que recibes como sueldo a fin de mes.',
			placeholder: ' Ej: 900.000',
			validation: yup
				.number()
				.required('Campo Requerido')
				.typeError('Campo Requerido')
				.min(600000, 'Sueldo  mínimo $600.000'),
		} as TextFieldLayoutItem,
		{
			label: 'Fecha de ingreso laboral',
			defaultValue: '',
			name: FORM.DATE_OF_EMPLOYMENT,
			type: LAYOUT_ITEM.TYPE.INPUT,
			mask: '##/####',
			placeholder: 'Ej: 04/2020',
			validation: yup
				.string()
				.required('Campo Requerido')
				.length(6, 'Ingrese una fecha válida')
				.matches(/^[0-9]+$/, 'Ingrese solo números')
				.test('date-limit-valid', 'Ingrese una fecha válida', (value) => {
					return value !== undefined ? DateStringValidator(value) : false;
				}),
		} as TextFieldLayoutItem,
		{
			defaultValue: 'Dependiente',
			type: LAYOUT_ITEM.TYPE.RADIO_BUTTON,
			label: 'Tipo de trabajo:',
			name: FORM.JOB_TYPE,
			options: [
				{
					label: 'Dependiente',
					value: 'Dependiente',
				},
				{
					label: 'Independiente',
					value: 'Independiente',
				},
			],
		} as RadioButtonLayoutItem,
		{
			label: 'Correo electronico',
			defaultValue: '',
			name: FORM.EMAIL,
			type: LAYOUT_ITEM.TYPE.INPUT,
			placeholder: 'Ej: mail@mail.cl',
			validation: yup.string().email('Ingrese un email válido').required('Campo Requerido'),
		} as TextFieldLayoutItem,
		{
			label: 'Teléfono',
			defaultValue: '',
			name: FORM.PHONE,
			type: LAYOUT_ITEM.TYPE.INPUT,
			prefix: '+56 ',
			placeholder: '9 8765 4321',
			mask: '# ### ### ##',
			validation: yup
				.number()
				.typeError('Ingrese un número válido')
				.min(111111111, 'Ingrese un número válido')
				.max(999999999, 'Ingrese un número válido')
				.notOneOf([999999999], 'Ingrese un número válido')
				.required('Campo Requerido'),
		} as TextFieldLayoutItem,
		{
			defaultValue: 'WHATSAPP',
			type: LAYOUT_ITEM.TYPE.RADIO_BUTTON,
			label: 'Prefiero que me contacten por:',
			name: FORM.TOUCH_POINT_SELECTED,
			options: [
				{
					label: 'WhatsApp',
					value: 'WHATSAPP',
				},
				{
					label: 'Correo electrónico',
					value: 'EMAIL',
				},
			]
		} as RadioButtonLayoutItem,
	];

	useEffect(
		() => {
			if (dataLayerKey) {
				TagManager.dataLayer({
					dataLayer: DATA_LAYERS.get(dataLayerKey),
				});
			}
		},
		//eslint-disable-next-line
		[]
	);

	return (
		<div>
			<MultiFormCardHeader
				title="Información personal"
				body="Completa tus datos para que te podamos evaluar y contactar correctamente."
			/>
			<CreateFormLayout key={fields.length} fields={fields} />
		</div>
	);
};
